if (!customElements.get("product-form")) {
  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();
        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";

        // Track cart update state
        this.isUpdating = false;
      }

      /**
       * Get base product info from the product page
       */
      getBaseProductInfo() {
        // Try different selectors for SKU
        const selectors = [
          "#Sku-template--17823921733782__main",
          "[data-product-sku]",
          "[data-sku]",
          ".product__sku",
        ];

        let sku = "";
        for (const selector of selectors) {
          const element = document.querySelector(selector);
          if (element) {
            sku = element.textContent.trim().replace("SKU:", "").trim();
            if (sku) break;
          }
        }

        // Fallback to variant ID if no SKU found
        if (!sku) {
          sku = this.variantIdInput.value;
        }

        return {
          sku,
          handle:
            window.location.pathname.split("/products/")[1]?.split("?")[0] ||
            "",
        };
      }

      /**
       * Get the selected add-ons from Infinite Options
       */
      getSelectedAddons() {
        const container = document.getElementById("infiniteoptions-container");
        if (!container) return [];

        const addons = [];
        const checkboxes = container.querySelectorAll(
          'input[type="checkbox"]:checked'
        );
        const { sku: baseSku } = this.getBaseProductInfo();

        checkboxes.forEach((checkbox) => {
          const label = checkbox.value;
          const priceInput = checkbox
            .closest(".Polaris-Stack")
            ?.querySelector('input[type="text"][aria-labelledby$="Label"]');
          const price = priceInput ? parseFloat(priceInput.value) : 0;

          // Try to find pre-configured variant ID for this add-on
          const variantInput = checkbox
            .closest(".spb-productoption")
            ?.querySelector('input[type="hidden"][data-variant-id]');
          const variantId = variantInput?.getAttribute("data-variant-id");

          addons.push({
            sku: baseSku,
            variantId: variantId || baseSku,
            label,
            price,
          });
        });

        return addons;
      }

      /**
       * Handle SparkLayer cart update for B2B customers
       */
      async handleSparkLayerSubmit(formData) {
        if (this.isUpdating) return;
        this.isUpdating = true;

        try {
          const { sku: baseSku, handle } = this.getBaseProductInfo();
          const baseQuantity = parseInt(formData.get("quantity") || 1);
          const addons = this.getSelectedAddons();

          // Build cart update with base product and add-ons
          const cartUpdate = {
            products: [
              // Base product
              {
                sku: baseSku,
                quantity: baseQuantity,
              },
              // Add-ons with proper variant IDs and attributes
              ...addons.map((addon) => ({
                sku: baseSku,
                quantity: 1,
                customAttributes: {
                  _addon: "true",
                  _addon_for: baseSku,
                  _addon_handle: handle,
                  option_label: addon.label,
                  option_price: addon.price.toString(),
                  option_type: "infinite_options",
                  variant_id: addon.variantId,
                },
              })),
            ],
          };

          // Use SparkLayer SDK to update cart
          await window.spark.updateCart(
            cartUpdate,
            false, // Show default success toast
            false // Show default error toast
          );

          // Update UI
          this.submitButton.classList.remove("loading");
          if (this.cart && this.cart.classList.contains("is-empty")) {
            this.cart.classList.remove("is-empty");
          }
          this.submitButton.removeAttribute("aria-disabled");
          this.querySelector(".loading__spinner").classList.add("hidden");
        } catch (error) {
          console.error("SparkLayer cart update failed:", error);
          this.handleErrorMessage("Failed to update cart. Please try again.");
          this.submitButton.classList.remove("loading");
          this.submitButton.removeAttribute("aria-disabled");
          this.querySelector(".loading__spinner").classList.add("hidden");
        } finally {
          this.isUpdating = false;
        }
      }

      /**
       * Handle form submission
       */
      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.handleErrorMessage();
        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const formData = new FormData(this.form);

        // Check if this is a B2B customer (SparkLayer is active)
        if (window.spark && window.sparkOptions) {
          this.handleSparkLayerSubmit(formData);
          return;
        }

        // Regular Shopify cart flow for B2C customers
        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    this.cart.renderContents(response);
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              this.cart.renderContents(response);
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add("hidden");
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute("disabled");
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}
